import { dateUtils, type Except } from '@festina-life/core'
import dayjs from 'dayjs'
import { type ChangeEvent, useCallback, useMemo } from 'react'

import { IconButton } from '../../actions/iconButton/IconButton'
import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'

// Safari's native date input has no built-in control to clear the value, and
// when empty it falls back to displaying the current date at low opacity instead
// of a placeholder. To work around this we render our own clear button, but only
// on Safari since other browsers already provide a clear button.
const isSafari =
  typeof navigator !== 'undefined' &&
  /safari/i.test(navigator.userAgent) &&
  !/chrome/i.test(navigator.userAgent)

export type RawDateInputProps = {
  min?: string
  max?: string
  /** Value date in server format, assuming UTC+00:00. */
  value?: string
  /** Callback for value date changes in server format, assuming UTC+00:00. */
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  // `type` is not exposed because only 'date' and 'datetime-local' are allowed.
  shouldAllowTime?: boolean
} & Except<RawTextInputProps, 'value' | 'onChange' | 'onBlur' | 'type'>

/** Date (time) input that can be used in Festina Forms to display a date input that follows our styling rules. May or may
 * not be wrapped by a FormInput component to add labels, descriptions and error messages around the input field.
 */
export const RawDateInput = ({
  value,
  editable = true,
  disabled,
  onChange,
  onBlur,
  shouldAllowTime,
  ...restProps
}: RawDateInputProps) => {
  // > All timestamps provided by the API’s are UTC time
  // See APGDEFECTS-1383?focusedCommentId=100099
  //
  // Therefore, the component must be careful to adjust for the offset of the user's
  // local timezone when submitting time (and only when submitting time, not plain dates!)
  const displayedValue = useMemo(
    () =>
      shouldAllowTime && value && value !== ''
        ? dateUtils
            .dateUtcAsLocal(value)
            .format(dateUtils.SERVER_DATE_TIME_FORMAT)
        : value,
    [shouldAllowTime, value],
  )

  const fixupDate = useCallback(
    (value: string) =>
      shouldAllowTime && value && value !== ''
        ? dayjs(value).utc().format(dateUtils.SERVER_DATE_TIME_FORMAT)
        : value,
    [shouldAllowTime],
  )

  const handleChange = useMemo(
    () =>
      onChange
        ? (e: ChangeEvent<HTMLInputElement>) =>
            onChange(fixupDate(e.target.value))
        : undefined,
    [fixupDate, onChange],
  )

  const handleBlur = useMemo(
    () =>
      onBlur
        ? (e: ChangeEvent<HTMLInputElement>) =>
            onBlur(fixupDate(e.target.value))
        : undefined,
    [fixupDate, onBlur],
  )

  const input = (
    <RawTextInput
      value={displayedValue}
      type={shouldAllowTime ? 'datetime-local' : 'date'}
      editable={editable}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      {...restProps}
    />
  )

  const shouldShowClearButton =
    isSafari && editable && !disabled && !!value && !!onChange

  if (!shouldShowClearButton) {
    return input
  }

  return (
    <div className="inline-block relative w-full">
      {input}
      <IconButton
        iconName="XCircle"
        onClick={() => onChange('')}
        className="absolute! right-0.5 top-1/2 -translate-y-1/2"
        aria-label="Clear date"
      />
    </div>
  )
}
